import React from 'react'
import cx from 'classnames'
import { Checkbox, Icon, isMobile, Lozenge, l } from '@manychat/manyui'

import { useAppSelector } from 'reduxTyped'
import { FlowContextMenu } from 'apps/cms/components/FlowContextMenu/FlowContextMenu'
import { getCmsFlowBadge } from 'apps/cms/components/Flows/components/FlowCard/utils/badgeType'
import { useExternalFlowCard } from 'apps/cms/components/Flows/hooks/useExternalFlowCard'
import { useFlowCardDrag } from 'apps/cms/components/Flows/hooks/useFlowCardDrag'
import { FlowCardClickAction } from 'apps/cms/pages/ContentManagementSystem/lib/types'
import { contentManagementSelectors } from 'apps/cms/store'
import { ICON_PRODUCT_NAME } from 'common/fusion/constants'
import { PermissionTargets, usePermission } from 'common/userRoles'
import { FsFlow } from 'shared/api/requests/cms/schemas'
import { useDndRef } from 'utils/commonHooks'

import iconBadge from '../../assets/iconBadge.svg'

import { ExternalFlowTriggers } from './ExternalFlowTriggers'

import cm from './FlowCard.module.css'

interface ExternalFlowCardProps {
  flow: FsFlow
  allowSelect: boolean
  isFlowSelected: boolean
  onToggleFlowSelection: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export function ExternalFlowCard({
  flow,
  allowSelect,
  isFlowSelected,
  onToggleFlowSelection,
}: ExternalFlowCardProps) {
  const { handleFlowCardClick, isCardDisabled, cardActionAriaLabel, errorMessage } =
    useExternalFlowCard({
      flow,
    })
  const userCanEditFlow = usePermission(PermissionTargets.FLOW_EDITING)
  const { connectDragSource } = useFlowCardDrag({ flow })
  const dragRef = useDndRef(connectDragSource)
  const {
    showFlowContextMenu,
    allowHoverFlowCard,
    allowHighlightActiveFlowCard,
    lastSelectedFlowId,
    flowCardClickAction,
  } = useAppSelector(contentManagementSelectors.getUi)
  const isFlowCardActive = lastSelectedFlowId === flow.ns && allowHighlightActiveFlowCard
  const showArrow = flowCardClickAction === FlowCardClickAction.OPEN_FLOW
  const ownerLabel = l.translate('Built in {productName}', {
    productName: ICON_PRODUCT_NAME,
  })

  return (
    <article
      ref={dragRef}
      className={cx(cm.card, {
        [cm.card_withoutContextMenu]: !userCanEditFlow || !showFlowContextMenu,
        [cm.card_disabled]: isCardDisabled,
        [cm.card_withHover]: allowHoverFlowCard,
        [cm.card_active]: isFlowCardActive,
      })}
      data-title-at="top"
      data-title={isCardDisabled && errorMessage && !isMobile ? errorMessage : undefined}
      data-test-id="flow-card"
    >
      {showArrow && (
        <button
          onClick={handleFlowCardClick}
          type="button"
          className={cm.card__mainActionButton}
          aria-label={cardActionAriaLabel}
        />
      )}

      {flowCardClickAction === FlowCardClickAction.SAVE_FLOW_ID_TO_STATE && (
        <button
          onClick={handleFlowCardClick}
          type="button"
          className={cm.card__mainActionButton}
          aria-label={cardActionAriaLabel}
        />
      )}

      {allowSelect && (
        <span className={cm.card__checkboxWrapper}>
          <Checkbox
            className={cm.card__interactableElement}
            checked={isFlowSelected}
            onChange={onToggleFlowSelection}
            ariaLabel={l.translate('Select flow')}
          />
        </span>
      )}

      <div className={cm.card__body}>
        <div className={cx(cm.card__content, cm.card__content_external)}>
          <div className={cx(cm.card__title, 'text-ellipsis')}>
            <Lozenge size="large" type={getCmsFlowBadge(flow)} />
            <h1 className={cx(cm.card__flowName, cm.card__flowName_accented, 'text-ellipsis')}>
              {flow.name}
            </h1>
          </div>

          <div className={cm.card__ownerActions}>
            <span
              className={cx(cm.card__ownerLabel, 'text-body-sm')}
              role="img"
              aria-label={ownerLabel}
            >
              <span className={cm.card__ownerLabelTile}>
                <img src={iconBadge} alt="" />
              </span>
              <span className={cm.card__ownerLabelText}>{ownerLabel}</span>
            </span>
            {showArrow && (
              <span className="d-flex text-default" aria-hidden="true">
                <Icon.ArrowTopRight size={20} />
              </span>
            )}
          </div>
          <div className={cm.card__contextMenuColumn}>
            {userCanEditFlow && showFlowContextMenu && (
              <FlowContextMenu
                flowPath={flow.path}
                anchorClassName={cx(cm.card__interactableElement, cm.card__contextMenuButton)}
                anchorOpenedClassName={cm.card__contextMenuButton_opened}
              />
            )}
          </div>
        </div>

        <ExternalFlowTriggers triggers={flow.triggers} onClick={handleFlowCardClick} />
      </div>
    </article>
  )
}
